import React, { useState, useEffect, useCallback } from 'react';
import { CheckCircle2, Sparkles, ChevronRight, X } from 'lucide-react';
import { getVideoProcessingStatus } from '../../services/videoActivationAPI';

const FONT_H = "'Lora', Georgia, serif";
const FONT_B = "'Source Sans 3', sans-serif";

const STATUS_CONFIG = {
  queued: { label: 'Queued for analysis', color: '#6B7280', bg: '#F3F4F6', icon: '\u23F3', animate: false },
  prescreening: { label: 'Checking video...', color: '#2563EB', bg: '#EFF6FF', icon: '\uD83D\uDD0D', animate: true },
  extracting_audio: { label: 'Extracting audio...', color: '#2563EB', bg: '#EFF6FF', icon: '\uD83C\uDFA4', animate: true },
  transcribing: { label: 'Transcribing speech...', color: '#7C3AED', bg: '#F5F3FF', icon: '\uD83D\uDCDD', animate: true },
  awaiting_confirmation: { label: 'Identify the speakers', color: '#D97706', bg: '#FFFBEB', icon: '\uD83D\uDC64', animate: false },
  confirmed: { label: 'Speakers confirmed', color: '#059669', bg: '#ECFDF5', icon: '\u2705', animate: false },
  extracting_moments: { label: 'Finding wisdom moments...', color: '#7C3AED', bg: '#F5F3FF', icon: '\u2728', animate: true },
  completed: { label: 'Analysis complete', color: '#059669', bg: '#ECFDF5', icon: '\u2705', animate: false },
  skipped: { label: 'Skipped', color: '#9CA3AF', bg: '#F9FAFB', icon: '\u23ED\uFE0F', animate: false },
  failed: { label: 'Analysis failed', color: '#DC2626', bg: '#FEF2F2', icon: '\u26A0\uFE0F', animate: false },
};

const SKIP_LABELS = {
  no_speech: 'No speech detected',
  too_few_words: 'Not enough speech content',
  unsupported_format: 'Unsupported video format',
};

const CATEGORY_STYLES = {
  life_advice: { bg: '#EAF1FB', color: '#3B72C4', label: 'Advice' },
  advice: { bg: '#EAF1FB', color: '#3B72C4', label: 'Advice' },
  family_history: { bg: '#FDF1E4', color: '#B5760A', label: 'Family History' },
  cultural_tradition: { bg: '#FDF1E4', color: '#B5760A', label: 'Tradition' },
  blessing: { bg: '#F1F5E6', color: '#556B2F', label: 'Blessing' },
  personal_story: { bg: '#F5F0FA', color: '#7C3AED', label: 'Story' },
  humor: { bg: '#FFF8E7', color: '#B8860B', label: 'Humor' },
  other: { bg: '#F3F4F6', color: '#6B7280', label: 'Other' },
};

const AVATAR_COLORS = ['#556B2F', '#8E6B23', '#3B72C4', '#6B3FA0', '#E65100', '#7B6B8E'];

function getAvatarColor(name) {
  return AVATAR_COLORS[name ? name.charCodeAt(0) % AVATAR_COLORS.length : 0];
}

function formatSpeakerNames(speakers) {
  if (!speakers || speakers.length === 0) return '';
  const names = speakers.map(s => s.name.split(' ')[0]); // first names
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} & ${names[1]}`;
  return names.slice(0, -1).join(', ') + ' & ' + names[names.length - 1];
}

// --- Wisdom Moments Modal ---
function WisdomMomentsModal({ moments, onClose }) {
  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(28,32,22,.55)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 20, maxWidth: 520, width: '100%',
          boxShadow: '0 30px 80px rgba(24,28,16,.4)', maxHeight: '70vh',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 20px', borderBottom: '1px solid #EFEDE2',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Sparkles size={18} color="#556B2F" />
            <span style={{ fontFamily: FONT_H, fontWeight: 700, fontSize: 18, color: '#2C2F26' }}>
              Wisdom moments from this video
            </span>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              width: 36, height: 36, display: 'flex', alignItems: 'center',
              justifyContent: 'center', borderRadius: 8, color: '#7C846A',
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Body — scrollable */}
        <div style={{ overflowY: 'auto', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {moments.map(moment => {
            const cat = CATEGORY_STYLES[moment.category] || CATEGORY_STYLES.other;
            return (
              <div key={moment.id} style={{
                border: '1px solid #EDEBDD', background: '#FCFCF7',
                borderRadius: 14, padding: '14px 16px',
              }}>
                {/* Top row: category + timestamp */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <span style={{
                    background: cat.bg, color: cat.color, fontSize: 12,
                    fontWeight: 600, padding: '3px 10px', borderRadius: 20,
                  }}>
                    {cat.label}
                  </span>
                  <span style={{ fontSize: 13, color: '#9AA088', fontFamily: FONT_B }}>{moment.ts}</span>
                </div>
                {/* Quote */}
                <p style={{
                  fontFamily: FONT_H, fontWeight: 500, fontStyle: 'italic',
                  fontSize: 16, color: '#2C2F26', lineHeight: 1.55,
                  margin: '0 0 12px',
                }}>
                  &ldquo;{moment.quote}&rdquo;
                </p>
                {/* Footer: speaker + link */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  {moment.speaker && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{
                        width: 22, height: 22, borderRadius: '50%',
                        background: getAvatarColor(moment.speaker.name),
                        color: '#fff', fontSize: 11, fontWeight: 700,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        {moment.speaker.initial}
                      </div>
                      <span style={{ fontSize: 14, color: '#4a4f40' }}>{moment.speaker.name}</span>
                    </div>
                  )}
                  <a
                    href={`/wisdom?moment=${moment.id}`}
                    style={{
                      fontSize: 13, color: '#556B2F', fontWeight: 600,
                      textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 2,
                    }}
                  >
                    Open in Wisdom Library <ChevronRight size={14} />
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// --- Main Component ---
const VideoProcessingBadge = ({ videoId, pollingInterval = 8000, onStatusChange }) => {
  const [statusData, setStatusData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (!videoId) return;
    let active = true;

    const prevStatusRef = { current: null };
    const fetchStatus = () => {
      getVideoProcessingStatus([videoId])
        .then(data => {
          if (!active) return;
          const newData = data[videoId] || null;
          const newStatus = newData?.status;
          if (prevStatusRef.current && newStatus !== prevStatusRef.current && onStatusChange) {
            onStatusChange(newStatus, prevStatusRef.current);
          }
          prevStatusRef.current = newStatus;
          setStatusData(newData);
          setLoading(false);
        })
        .catch(() => { if (active) setLoading(false); });
    };

    fetchStatus();
    const interval = setInterval(() => {
      if (!active) return;
      if (statusData && ['completed', 'skipped', 'failed'].includes(statusData.status)) return;
      fetchStatus();
    }, pollingInterval);

    return () => { active = false; clearInterval(interval); };
  }, [videoId, pollingInterval, statusData?.status]);

  if (loading || !statusData) return null;

  const config = STATUS_CONFIG[statusData.status] || STATUS_CONFIG.queued;
  const identifiedSpeakers = statusData.identified_speakers || [];
  const wisdomMoments = statusData.wisdom_moments || [];
  const momentCount = statusData.wisdom_moments_count || 0;
  const allIdentified = identifiedSpeakers.length > 0;

  // Determine badge label
  let label = config.label;
  if (statusData.status === 'completed' && momentCount > 0) {
    label = 'Analysis complete';
  } else if (statusData.status === 'completed' && statusData.wisdom_eligible === false) {
    label = 'Transcript ready';
  } else if (statusData.status === 'skipped' && statusData.skip_reason) {
    label = SKIP_LABELS[statusData.skip_reason] || config.label;
  }

  return (
    <div>
      {/* Status badge (processing states) */}
      {!allIdentified && (
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '4px 10px', borderRadius: 20,
          background: config.bg, border: `1px solid ${config.color}22`,
          fontSize: 12, fontWeight: 500, color: config.color, marginTop: 6,
        }}>
          <span style={{ fontSize: 13, animation: config.animate ? 'pulse 1.5s ease-in-out infinite' : 'none' }}>
            {config.icon}
          </span>
          <span>{label}</span>
        </div>
      )}

      {/* Voices identified row — replaces status badge once all speakers confirmed */}
      {allIdentified && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '13px 16px', borderBottom: '1px solid #EFEDE2',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {/* Avatar stack */}
            <div style={{ display: 'flex' }}>
              {identifiedSpeakers.map((speaker, i) => (
                <div key={speaker.id} style={{
                  width: 26, height: 26, borderRadius: '50%',
                  background: getAvatarColor(speaker.name),
                  color: '#fff', fontSize: 11, fontWeight: 700,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  border: '2px solid #fff',
                  marginLeft: i > 0 ? -8 : 0,
                  zIndex: identifiedSpeakers.length - i,
                  position: 'relative',
                }}>
                  {speaker.initial}
                </div>
              ))}
            </div>
            {/* Names label */}
            <span style={{ fontSize: 14, color: '#4a4f40', fontFamily: FONT_B }}>
              <strong style={{ color: '#2C2F26' }}>{formatSpeakerNames(identifiedSpeakers)}</strong>
              {' '}&middot; voices identified
            </span>
          </div>
          <CheckCircle2 size={18} color="#556B2F" />
        </div>
      )}

      {/* Wisdom moments button — only when moments exist */}
      {momentCount > 0 && (
        <button
          onClick={() => setShowModal(true)}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            width: '100%', background: '#F1F5E6', border: '1px solid #C8D3AE',
            color: '#3E5220', borderRadius: 999, padding: '9px 14px',
            fontWeight: 700, fontSize: 14.5, cursor: 'pointer',
            fontFamily: FONT_B, minHeight: 44, marginTop: 8,
          }}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Sparkles size={16} color="#556B2F" />
            {momentCount} wisdom moment{momentCount !== 1 ? 's' : ''} found
          </span>
          <ChevronRight size={16} color="#556B2F" />
        </button>
      )}

      {/* Modal */}
      {showModal && wisdomMoments.length > 0 && (
        <WisdomMomentsModal
          moments={wisdomMoments}
          onClose={() => setShowModal(false)}
        />
      )}

      {config.animate && (
        <style>{`@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }`}</style>
      )}
    </div>
  );
};

export default VideoProcessingBadge;
